import { Component, OnInit, OnDestroy, Input, Output, ViewChild, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subscription, pipe, finalize, concatMap, Subject, of } from 'rxjs';
import { MenuItem, MessageService, FilterService } from 'primeng/api';
import { map, filter, switchMap, debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { ProductApiService } from 'src/app/services/product-api.service';
import { environment } from 'src/environments/environment';

/**
 * Reusable product global-category dropdown selector backed by `ProductApiService`.
 *
 * Two pipelines run in parallel after init:
 * - `load$` — debounced `switchMap(getProductCategoryByID)` — resolves the currently assigned
 *   global category for the given `productId` and emits it via `selectedProductCategory`.
 * - `area$` — debounced `switchMap(emit)` — emits `selectedProductCategory` after a user
 *   selects a different category from the dropdown.
 *
 * On init `_reloadComponentData()` fetches the full sorted list of global categories for the
 * dropdown options, and `load$.next(productId)` triggers the initial product-specific lookup.
 *
 * On `productId` input change the component pushes a new ID onto `load$` without reloading the
 * static category list.
 *
 * **Output:** `selectedProductCategory` emits the selected category string on load and on user change.
 */
@Component({
  selector: 'app-product-global-category-selector',
  templateUrl: './product-global-category-selector.component.html',
  styleUrls: ['./product-global-category-selector.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ProductGlobalCategorySelectorComponent implements OnInit, OnChanges, OnDestroy {

  _loading: boolean;

  /** Full sorted list of global category strings used to populate the dropdown. */
  productGlobalCategories: string[] = [];
  /** Currently selected global category string. */
  productCategory: string = '';

  /** The product ID used to look up the product's currently assigned global category. */
  @Input() productId: number;

  /** Label text displayed above the dropdown. */
  @Input() labelKey = 'Global category';
  /** Placeholder text shown inside the dropdown when nothing is selected. */
  @Input() placeholder = 'Select category...';

  _disabled: boolean = false;
  /** When `true`, the dropdown is rendered as read-only. */
  @Input() get disabled(): boolean { return this._disabled; }
  set disabled(b: boolean) { this._disabled = b; }

  _showLabel: boolean = true;
  /** When `false`, the label above the dropdown is hidden. */
  @Input() get showLabel(): boolean { return this._showLabel; }
  set showLabel(b: boolean) { this._showLabel = b; }

  /** Emits the selected global-category string on initial product load and on user selection. */
  @Output() public selectedProductCategory: EventEmitter<string> = new EventEmitter();

  /** When `true`, the Edit button is rendered. */
  @Input() showEditButton: boolean = true;
  /** When `true`, the Add Rate button is rendered. */
  @Input() showAddRateButton: boolean = true;
  /** When `true`, the Add Category button is rendered. */
  @Input() showAddCategoryButton: boolean = true;

  public get doShowEditButton(): boolean { return this.showEditButton; };
  public get doShowAddRateButton(): boolean { return this.showAddRateButton; };
  public get doShowAddCategoryButton(): boolean { return this.showAddCategoryButton; };

  /** `true` when `productCategory` matches an entry in the loaded `productGlobalCategories` list. */
  public get areaExists(): boolean { return this.productGlobalCategories.find(e => e == this.productCategory) != null; };

  /** Pushes `productId` changes to trigger a product-specific category lookup. */
  private load$ = new Subject<number>();
  /** Pushes user-selected category strings to trigger `selectedProductCategory` emit. */
  private area$ = new Subject<string>();

  private _getCategoryQuery: Subscription;
  private _selCategoryQuery: Subscription;
  private _gelCategoryQuery: Subscription;

  constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
) { }

  /** Reserved for post-load category resolution — currently a no-op (body is commented out). */
  private selectRow(): void {

    //    this.productCategory = (this._selectedID && this._currList) ? this._currList.find(x => x.CURID == this._selectedID) : null;

    //    console.log('*** ProductGlobalCategorySelectorComponent _selectCategory: ' + JSON.stringify(this._selectedCategory));
    //    this.emitCategory();
  }

  /** Loads the full sorted list of global categories (for dropdown options) from the API. */
  private _reloadComponentData(): void {
    this._loading = true;
    this._getCategoryQuery =
      this.prodApi.getProductGlobalCategogoryList()
        .pipe(
          //tap(a => console.log('*** ProductGlobalCategorySelectorComponent productGlobalCategories: ' + JSON.stringify(a))),
          //tap(a => console.table(a)),
          concatMap(data => this.productGlobalCategories = data.map(a => a['ProductCategory']).sort()),

          finalize(() => {
            this._loading = false;
            this.selectRow();
          })
        )
        .subscribe();
  }

  /** Reacts to `productId` changes by pushing the new ID onto `load$` for a product-specific lookup. */
  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** ProductGlobalCategorySelectorComponent ngOnChanges');
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** ProductGlobalCategorySelectorComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));
      //console.log('*** ProductGlobalCategorySelectorComponent ngOnChanges (was):    ' + propName + ' ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'productId':
          this.load$.next(change.currentValue);
          break;
      }
    }
  }

  /** Unsubscribes all active pipeline subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    if (this._getCategoryQuery) { this._getCategoryQuery.unsubscribe(); }
    if (this._selCategoryQuery) { this._selCategoryQuery.unsubscribe(); }
    if (this._gelCategoryQuery) { this._gelCategoryQuery.unsubscribe(); }
  }

  /** Sets up the `load$` and `area$` pipelines, loads the category list, and triggers the initial product lookup. */
  ngOnInit(): void {
    console.log('*** ProductGlobalCategorySelectorComponent ngOnInit: ');

    this._selCategoryQuery = this.load$
      .pipe(

        debounceTime(50),
        distinctUntilChanged(),

        tap(() => this._loading = true),

        switchMap(prodid => this.prodApi.getProductCategoryByID(prodid).pipe(
          tap(a => environment.production || console.log('*** ProductGlobalCategorySelectorComponent _reloadComponentData: ' + JSON.stringify(a))),
          //filter(cat => cat != null && cat.length > 0),

          tap(data => environment.production || console.log('*** ProductGlobalCategorySelectorComponent _reloadComponentData: ' + JSON.stringify(data.map(cat => cat['ProductCategory'])[0]))),
          map(data => this.productCategory = data.map(cat => cat['ProductCategory'])[0] || null),
          tap(data => environment.production || console.log('*** ProductGlobalCategorySelectorComponent this.load$: ' + JSON.stringify(data))),

          finalize(() => {
            this._loading = false;
            this.selectedProductCategory.emit(this.productCategory);
          })
        )),
      )
      .subscribe();

    this._gelCategoryQuery = this.area$
      .pipe(

        debounceTime(150),
        distinctUntilChanged(),

        switchMap(
          text => of(text).pipe(
            tap(text => environment.production || this.messageService.add({ severity: 'info', summary: text, life: 10000, detail: JSON.stringify(text) }) ),
            tap(text => this.selectedProductCategory.emit(text))
        )),
      )
      .subscribe();

    this._reloadComponentData();

    this.load$.next(this.productId);
  }

  /** Handles dropdown `(onClick)` — currently a no-op. */
  onClickCS(e) {
    environment.production || console.log('*** ProductGlobalCategorySelectorComponent onClickCS: ' + JSON.stringify(e))
  }

  /** Handles dropdown `(onChange)` — pushes the selected value onto `area$` to emit `selectedProductCategory`. */
  onChangeCS(e) {
    environment.production || console.log('*** ProductGlobalCategorySelectorComponent onChangeCS: ' + JSON.stringify(e));

    this.area$.next(e.value);
  }

  /** Add-category button handler — currently a no-op (body is empty). */
  onCategoryAddButton(e) {
    environment.production || console.log('*** ProductGlobalCategorySelectorComponent onCategoryAddButton: ' + JSON.stringify(e));
    //console.log('*** ProductGlobalCategorySelectorComponent onCategoryAddButton: about to add currency ');
  }
}
